import { useApolloClient } from '@apollo/client'
import type { ViewQueryList, ViewQueryNode } from '@salescore/core'
import { message } from 'antd'
import { isUndefined } from 'effect/Predicate'
import { isFailure } from 'effect/Result'
import { t } from 'i18next'
import { useSetRecoilState } from 'recoil'

import { applyMagnifyingGlassChange } from '../../../domain/useUpsertViewRecordsEntry/applyMagnifyingGlassChange'
import { useUpsertViewRecordsEntry } from '../../../hooks/useUpsertViewRecordsEntry'
import { toMutable } from '../../../rsheet/types/DeepReadonly'
import { useRecordsImpl, useSetRecordsImpl } from '../../records'
import { changesAtom } from '../../recordsHistory'
import type { SalescoreObjectAbility, ViewAbility } from '../../view/atoms'
import { fetchChildRecordNode } from './fetchChildNode'
import { getParentReferenceField } from './getParentReferenceField'
import { useEvaluateFieldCallbacks } from './useEvaluateFieldCallbacks'

interface Argument {
  rowIndex: number
  innerRowIndex: number
  node: ViewQueryNode
  id: string
}

//
// 虫眼鏡セルをクリックして値を選択した時に呼び出す
// 子レコードを追加し、親ノードのfieldを更新する
//
export const useApplyMagnifyingGlassChange = (
  query: ViewQueryList | undefined,
  ability: ViewAbility & SalescoreObjectAbility,
) => {
  const client = useApolloClient()
  const previousRecords = useRecordsImpl()
  const setRecords = useSetRecordsImpl()
  const setChanges = useSetRecoilState(changesAtom)
  const evaluateFieldCallbacks = useEvaluateFieldCallbacks(query)
  const upsertViewRecordsEntry = useUpsertViewRecordsEntry(query, ability)

  return async (argument: Argument): Promise<void> => {
    if (query === undefined) {
      // query が undefined なのは kpi/kpiPivot view 経由で本 hook が mount された場合のみ。
      // kpi/kpiPivot の column 型では本分岐 (虫眼鏡セル選択) に到達しないため silent return で安全。
      return
    }
    const { node, id } = argument

    const getParentEither = getParentReferenceField(query, node)
    if (isFailure(getParentEither)) {
      throw new Error(getParentEither.failure)
    }
    const getParentResult = getParentEither.success

    const fetchedChildNode = await fetchChildRecordNode(node, id, query, client, getParentResult.parentReferenceField)
    if (fetchedChildNode === undefined) {
      void message.warning(t(`レコードが見つかりませんでした`))
      return
    }

    const applyEither = applyMagnifyingGlassChange(previousRecords, query, {
      ...argument,
      parentReferenceField: getParentResult.parentReferenceField,
      joinOnId: getParentResult.join.joinOn,
      parentNode: getParentResult.parentNode,
      fetchedChildNode,
    })

    if (isFailure(applyEither)) {
      if (isUndefined(applyEither.failure)) {
        return
      }
      throw new Error(applyEither.failure.message)
    }

    const applyResult = applyEither.success
    if (applyResult === 'referenceAlreadyUptoDate') {
      return
    }
    const { records, change } = applyResult
    setRecords(toMutable(records))
    setChanges((oldChanges) => [...oldChanges, change])

    const callbackRecordChanges = await evaluateFieldCallbacks(change)
    if (callbackRecordChanges.length > 0) {
      upsertViewRecordsEntry({
        recordChanges: callbackRecordChanges,
        recordsAfterHeightAdjustment: toMutable(records),
      })
    }
  }
}
